import { type AnyAction } from '@reduxjs/toolkit';

import {
  TokenRefreshTimeoutError,
  InvalidTokenError,
} from 'embeddings/HostMessageControllerErrors';

/**
 * Type for actions that can be checked by the matcher functions.
 * Actions can have payload (RTK Query), error (async thunks), or both.
 */
type ActionToCheck = AnyAction & {
  type: string;
  payload?: {
    status?: number;
    [key: string]: unknown;
  };
  error?: {
    status?: number;
    name?: string;
    message?: string;
    [key: string]: unknown;
  };
};

/**
 * Checks if an action error is a 401 Unauthorized error
 * @param action - Redux action object
 * @returns True if the action represents a 401 error
 */
export function isInvalidTokenRejection(action: ActionToCheck): boolean {
  if (action.type.startsWith('fusion/') && action.type.endsWith('/rejected')) {
    // these are rejections dispatched by the fusionApi with RTK Query
    const status = action.payload?.status;
    return status !== undefined && [401, 418].includes(status);
  }

  // match on Axios errors thrown within an async thunk
  const errorStatus = action.error?.status;
  const errorName = action.error?.name;
  const errorMessage = action.error?.message;
  return (
    (errorStatus !== undefined && [401, 418].includes(errorStatus)) || // errors that have been serialized with thunkErrorSerializer will have the status property
    (errorName === 'AxiosError' && // errors that use the default serializer can only be matched by name and message
      errorMessage !== undefined &&
      ['Request failed with status code 418', 'Request failed with status code 401'].includes(
        errorMessage
      ))
  );
}

/**
 * Checks if an action error is a TokenRefreshTimeoutError
 * @param action - Redux action object
 * @returns True if the action represents a token refresh timeout error
 */
export function isTokenRefreshTimeout(action: ActionToCheck): boolean {
  return action.error?.name === TokenRefreshTimeoutError.name;
}

/**
 * Checks if an action error is an InvalidTokenError
 * @param action - Redux action object
 * @returns True if the action represents an invalid token error
 */
export function isInvalidToken(action: ActionToCheck): boolean {
  return action.error?.name === InvalidTokenError.name;
}
